import { supabase } from '../lib/supabase';

export interface PlatformMetrics {
  totalBusinesses: number;
  activeBusinesses: number;
  totalUsers: number;
  activeUsers: number;
  totalBookings: number;
  monthlyBookings: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
}

export interface BusinessSummary {
  id: string;
  name: string;
  owner_email: string;
  owner_name: string;
  type: string;
  created_at: string;
  user_count: number;
  booking_count: number;
  sales_amount: number;
  last_activity: string;
  status: 'active' | 'inactive' | 'suspended';
  trial_enabled?: boolean;
  trial_end_date?: string;
  integrations: {
    twilio: boolean;
    gmail: boolean;
    stripe: boolean;
  };
}

export interface UserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  business_name: string;
  business_id: string;
  created_at: string;
  last_sign_in_at: string;
  is_business_owner: boolean;
}

export class SuperAdminService {
  /**
   * Get platform-wide metrics overview
   */
  static async getPlatformMetrics(): Promise<PlatformMetrics | null> {
    if (!supabase) return null;

    try {
      // Get total businesses
      const { count: totalBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true });

      // Get active businesses (those with activity in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: activeBusinesses } = await supabase
        .from('businesses')
        .select('*', { count: 'exact', head: true })
        .gte('updated_at', thirtyDaysAgo.toISOString());

      // Get total users from auth.users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });

      // Get active users (signed in within last 30 days)
      const { count: activeUsers } = await supabase
        .rpc('count_active_users', { days_threshold: 30 });

      // Get total bookings
      const { count: totalBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false);

      // Get monthly bookings
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      const { count: monthlyBookings } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', false)
        .gte('created_at', monthStart.toISOString());

      // Simple system health check (can be enhanced later)
      const systemHealth: 'healthy' | 'warning' | 'critical' = 
        (activeBusinesses || 0) > 0 && (activeUsers || 0) > 0 ? 'healthy' : 'warning';

      return {
        totalBusinesses: totalBusinesses || 0,
        activeBusinesses: activeBusinesses || 0,
        totalUsers: totalUsers || 0,
        activeUsers: activeUsers || 0,
        totalBookings: totalBookings || 0,
        monthlyBookings: monthlyBookings || 0,
        systemHealth
      };
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      return null;
    }
  }

  /**
   * Get list of all businesses with summary data
   */
  static async getAllBusinesses(): Promise<BusinessSummary[]> {
    if (!supabase) return [];

    try {
      console.log('🏢 Fetching all businesses...');
      
      // First, let's see ALL Gmail connections in the database (Super Admin should see everything)
      const { data: allGmailConnections, error: allGmailError } = await supabase
        .from('gmail_connections')
        .select('id, business_id, is_active, email')
        .order('created_at', { ascending: false });
      
      console.log('🔍 ALL Gmail connections in database:', allGmailConnections);
      if (allGmailError) {
        console.error('❌ Error fetching ALL Gmail connections:', allGmailError);
      }
      
      // First, get basic business data
      const { data: businesses, error } = await supabase
        .from('businesses')
        .select('id, name, type, created_at, updated_at, owner_id, trial_enabled, trial_end_date')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching businesses:', error);
        throw error;
      }

      console.log('✅ Found businesses:', businesses?.length || 0);

      if (!businesses || businesses.length === 0) {
        console.log('⚠️ No businesses found in database');
        return [];
      }

      // Get summary data for each business
      const businessSummaries: BusinessSummary[] = [];

      for (const business of businesses) {
        try {
          // Get owner information
          let ownerEmail = '';
          let ownerName = '';
          
          if (business.owner_id) {
            const { data: owner, error: ownerError } = await supabase
              .from('users')
              .select('email, first_name, last_name')
              .eq('id', business.owner_id)
              .single();
              
            if (!ownerError && owner) {
              ownerEmail = owner.email || '';
              ownerName = `${owner.first_name || ''} ${owner.last_name || ''}`.trim();
            }
          }

          // Get active resource count for this business
          const { count: activeResourceCount } = await supabase
            .from('resources')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', business.id)
            .eq('status', 'active');

          // Get booking count and sales data for this business
          const { count: bookingCount } = await supabase
            .from('bookings')
            .select('*', { count: 'exact', head: true })
            .eq('business_id', business.id)
            .eq('is_deleted', false);

          // Calculate total sales (subtotal - discount_amount) for this business
          const { data: bookingsData } = await supabase
            .from('bookings')
            .select('subtotal, discount_amount, status')
            .eq('business_id', business.id)
            .eq('is_deleted', false)
            .neq('status', 'cancelled');

          const salesAmount = (bookingsData || []).reduce((sum, booking) => {
            const subtotal = booking.subtotal || 0;
            const discountAmount = booking.discount_amount || 0;
            return sum + (subtotal - discountAmount);
          }, 0);

          // Get subscription status and plan from business
          const { data: businessData } = await supabase
            .from('businesses')
            .select('subscription_status, active_plan_id, updated_at')
            .eq('id', business.id)
            .single();

          const subscriptionStatus = businessData?.subscription_status;
          const activePlanId = businessData?.active_plan_id;
          const lastActivity = businessData?.updated_at || business.updated_at;
          
          // Get plan name from plan ID
          const getPlanName = (planId: string | null): string => {
            if (!planId) return 'No Plan';
            switch (planId) {
              case 'surge': return 'Surge Plan';
              case 'core': return 'Core Plan';
              case 'scale': return 'Scale Plan';
              case 'essential': return 'Essential Plan';
              case 'professional': return 'Professional Plan';
              default: return `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`;
            }
          };
          
          // Determine status based on subscription status
          let status: 'active' | 'inactive' | 'suspended';
          
          if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
            status = 'active';
          } else if (subscriptionStatus === 'past_due') {
            status = 'inactive';
          } else if (subscriptionStatus === 'canceled' || subscriptionStatus === 'inactive') {
            status = 'suspended';
          } else {
            // Fallback: check if business has any recent activity (for businesses without subscription data)
            const { data: lastBooking } = await supabase
              .from('bookings')
              .select('created_at')
              .eq('business_id', business.id)
              .eq('is_deleted', false)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();

            const lastBookingDate = lastBooking?.created_at;
            const daysSinceActivity = lastBookingDate ? 
              Math.floor((Date.now() - new Date(lastBookingDate).getTime()) / (1000 * 60 * 60 * 24)) : 
              999; // No activity
            
            status = daysSinceActivity < 7 ? 'active' : 
                    daysSinceActivity < 30 ? 'inactive' : 'suspended';
          }

          // Check integrations (Super Admin should have full access to all data)
          console.log(`🔍 Checking integrations for business: ${business.name} (ID: ${business.id})`);
          
          const [
            { data: gmailConnections, error: gmailError },
            { data: smtpConfig, error: smtpError },
            { data: twilioSettings, error: twilioError },
            { data: stripeConnectSettings, error: stripeError }
          ] = await Promise.all([
            // Check Gmail integration (gmail_connections table) - Super Admin should see all connections
            supabase
              .from('gmail_connections')
              .select('id, is_active, business_id')
              .eq('business_id', business.id),
            
            // Check SMTP config for Gmail OAuth
            supabase
              .from('smtp_config')
              .select('use_oauth, oauth_access_token, oauth_refresh_token')
              .eq('business_id', business.id)
              .maybeSingle(),
            
            // Check Twilio integration
            supabase
              .from('businesses')
              .select('twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
              .eq('id', business.id)
              .maybeSingle(),
            
            // Check Stripe Connect integration (stored in businesses table)
            supabase
              .from('businesses')
              .select('stripe_connect_account_id, stripe_connect_onboarding_completed, stripe_connect_charges_enabled')
              .eq('id', business.id)
              .maybeSingle()
          ]);
          
          // Check if any Gmail connection is active
          const hasActiveGmailConnection = gmailConnections && gmailConnections.some(conn => conn.is_active === true);
          
          console.log(`📧 Gmail connections for ${business.name}:`, {
            businessId: business.id,
            gmailConnections,
            gmailError,
            hasActiveGmailConnection,
            connectionCount: gmailConnections?.length || 0,
            activeConnections: gmailConnections?.filter(conn => conn.is_active === true).length || 0,
            query: `SELECT id, is_active FROM gmail_connections WHERE business_id = '${business.id}'`
          });
          
          // If there's an error, log it specifically
          if (gmailError) {
            console.error(`❌ Gmail query error for ${business.name}:`, gmailError);
          }
          
          // Let's also check what ALL Gmail connections look like in the database
          if (business.name.toLowerCase().includes('x detail') || business.name.toLowerCase().includes('ccr') || business.name.toLowerCase().includes('swyf')) {
            console.log(`🔍 DETAILED DEBUG for ${business.name}:`, {
              businessId: business.id,
              allGmailConnections: gmailConnections,
              hasActiveConnection: hasActiveGmailConnection,
              activeConnections: gmailConnections?.filter(conn => conn.is_active === true) || []
            });
          }

          const integrations = {
            gmail: !!(hasActiveGmailConnection || (smtpConfig?.use_oauth && smtpConfig?.oauth_access_token)),
            twilio: !!(twilioSettings?.twilio_enabled && 
                      twilioSettings?.twilio_account_sid && 
                      twilioSettings?.twilio_auth_token && 
                      twilioSettings?.VITE_TWILIO_PHONE_NUMBER),
            stripe: !!(stripeConnectSettings?.stripe_connect_account_id && 
                      stripeConnectSettings?.stripe_connect_onboarding_completed)
          };

          // Debug logging for integration status
          console.log(`🔍 Integration status for ${business.name || 'Unnamed Business'}:`, {
            gmail: integrations.gmail,
            twilio: integrations.twilio,
            stripe: integrations.stripe,
            hasActiveGmailConnection,
            smtpConfig: {
              useOauth: smtpConfig?.use_oauth,
              hasAccessToken: !!smtpConfig?.oauth_access_token,
              hasRefreshToken: !!smtpConfig?.oauth_refresh_token
            },
            twilioSettings: {
              enabled: twilioSettings?.twilio_enabled,
              hasAccountSid: !!twilioSettings?.twilio_account_sid,
              hasAuthToken: !!twilioSettings?.twilio_auth_token,
              hasPhoneNumber: !!twilioSettings?.VITE_TWILIO_PHONE_NUMBER
            },
            stripeSettings: {
              hasAccountId: !!stripeConnectSettings?.stripe_connect_account_id,
              onboardingCompleted: stripeConnectSettings?.stripe_connect_onboarding_completed
            }
          });
          
          // Additional debugging for Gmail detection
          console.log(`📧 Gmail detection details for ${business.name}:`, {
            gmailConnectionExists: hasActiveGmailConnection,
            smtpOauthEnabled: smtpConfig?.use_oauth,
            smtpHasAccessToken: !!smtpConfig?.oauth_access_token,
            finalGmailStatus: integrations.gmail
          });

          businessSummaries.push({
            id: business.id,
            name: business.name || 'Unnamed Business',
            owner_email: ownerEmail,
            owner_name: ownerName,
            type: getPlanName(activePlanId),
            created_at: business.created_at,
            user_count: activeResourceCount || 0,
            booking_count: bookingCount || 0,
            sales_amount: salesAmount,
            last_activity: lastActivity,
            status,
            trial_enabled: business.trial_enabled,
            trial_end_date: business.trial_end_date,
            integrations
          });
        } catch (businessError) {
          console.error(`❌ Error processing business ${business.id}:`, businessError);
          // Continue with other businesses even if one fails
        }
      }

      console.log('✅ Processed business summaries:', businessSummaries.length);
      return businessSummaries;
    } catch (error) {
      console.error('❌ Error fetching businesses:', error);
      return [];
    }
  }

  /**
   * Get business details by ID
   */
  static async getBusinessDetails(businessId: string): Promise<any> {
    if (!supabase) return null;

    try {
      console.log('🏢 Fetching business details for:', businessId);
      
      const { data: business, error } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', businessId)
        .single();

      if (error) {
        console.error('❌ Error fetching business details:', error);
        throw error;
      }

      // Get owner information separately
      let owner = null;
      if (business.owner_id) {
        const { data: ownerData, error: ownerError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, created_at')
          .eq('id', business.owner_id)
          .single();
          
        if (!ownerError && ownerData) {
          owner = ownerData;
        }
      }

      // Get additional business data
      const [
        { count: activeResourceCount },
        { count: bookingCount },
        { count: serviceCount },
        { count: customerCount },
        { data: bookingsData }
      ] = await Promise.all([
        supabase.from('resources').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('status', 'active'),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('is_deleted', false),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('bookings').select('subtotal, discount_amount, status').eq('business_id', businessId).eq('is_deleted', false).neq('status', 'cancelled')
      ]);

      // Calculate total sales
      const salesAmount = (bookingsData || []).reduce((sum, booking) => {
        const subtotal = booking.subtotal || 0;
        const discountAmount = booking.discount_amount || 0;
        return sum + (subtotal - discountAmount);
      }, 0);

      // Get plan name from plan ID
      const getPlanName = (planId: string | null): string => {
        if (!planId) return 'No Plan';
        switch (planId) {
          case 'surge': return 'Surge Plan';
          case 'core': return 'Core Plan';
          case 'scale': return 'Scale Plan';
          case 'essential': return 'Essential Plan';
          case 'professional': return 'Professional Plan';
          default: return `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`;
        }
      };

      console.log('✅ Business details loaded successfully');

      return {
        ...business,
        users: owner, // For compatibility with existing code
        planName: getPlanName(business.active_plan_id),
        metrics: {
          userCount: activeResourceCount || 0,
          bookingCount: bookingCount || 0,
          serviceCount: serviceCount || 0,
          customerCount: customerCount || 0,
          salesAmount: salesAmount
        }
      };
    } catch (error) {
      console.error('❌ Error fetching business details:', error);
      return null;
    }
  }

  /**
   * Get all users across all businesses
   */
  static async getAllUsers(): Promise<UserSummary[]> {
    if (!supabase) return [];

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return users?.map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '', // Would need to get from auth.users
          is_business_owner: false // Would need to check businesses.owner_id
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching all users:', error);
      return [];
    }
  }

  /**
   * Get recent users across all businesses
   */
  static async getRecentUsers(limit: number = 10): Promise<UserSummary[]> {
    if (!supabase) return [];

    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return users?.map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '', // Would need to get from auth.users
          is_business_owner: false // Would need to check businesses.owner_id
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching recent users:', error);
      return [];
    }
  }

  /**
   * Search for businesses or users
   */
  static async searchPlatform(query: string): Promise<{
    businesses: BusinessSummary[];
    users: UserSummary[];
  }> {
    if (!supabase || !query.trim()) {
      return { businesses: [], users: [] };
    }

    try {
      // Search businesses
      const { data: businesses } = await supabase
        .from('businesses')
        .select(`
          id,
          name,
          type,
          created_at,
          updated_at,
          subscription_status,
          active_plan_id,
          twilio_enabled,
          twilio_account_sid,
          twilio_auth_token,
          VITE_TWILIO_PHONE_NUMBER,
          stripe_connect_account_id,
          stripe_connect_onboarding_completed,
          users!businesses_owner_id_fkey(
            email,
            first_name,
            last_name
          )
        `)
        .or(`name.ilike.%${query}%,type.ilike.%${query}%`)
        .limit(10);

      // Get Gmail connections and SMTP configs for searched businesses
      const searchedBusinessIds = (businesses || []).map(b => b.id);
      const [
        { data: searchGmailConnections },
        { data: searchSmtpConfigs }
      ] = await Promise.all([
        supabase
          .from('gmail_connections')
          .select('business_id')
          .eq('is_active', true)
          .in('business_id', searchedBusinessIds),
        supabase
          .from('smtp_config')
          .select('business_id, use_oauth, oauth_access_token')
          .in('business_id', searchedBusinessIds)
      ]);

      // Create sets for quick lookup
      const searchGmailConnectedBusinessIds = new Set(searchGmailConnections?.map(gc => gc.business_id) || []);
      const searchSmtpOauthBusinessIds = new Set(
        searchSmtpConfigs?.filter(sc => sc.use_oauth && sc.oauth_access_token).map(sc => sc.business_id) || []
      );
      
      // Debug logging for search integration detection
      console.log('🔍 Search integration detection:', {
        searchedBusinessIds,
        searchGmailConnections: searchGmailConnections?.length || 0,
        searchSmtpConfigs: searchSmtpConfigs?.length || 0,
        searchGmailConnectedBusinessIds: Array.from(searchGmailConnectedBusinessIds),
        searchSmtpOauthBusinessIds: Array.from(searchSmtpOauthBusinessIds)
      });

      // Search users
      const { data: users } = await supabase
        .from('users')
        .select(`
          id,
          email,
          first_name,
          last_name,
          created_at,
          businesses(
            id,
            name
          )
        `)
        .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(10);



      // Transform business data
      const businessSummaries: BusinessSummary[] = (businesses || []).map(business => {
        const owner = Array.isArray(business.users) ? business.users[0] : business.users;
        
        // Get plan name from plan ID
        const getPlanName = (planId: string | null): string => {
          if (!planId) return 'No Plan';
          switch (planId) {
            case 'surge': return 'Surge Plan';
            case 'core': return 'Core Plan';
            case 'scale': return 'Scale Plan';
            case 'essential': return 'Essential Plan';
            case 'professional': return 'Professional Plan';
            default: return `${planId.charAt(0).toUpperCase() + planId.slice(1)} Plan`;
          }
        };
        
        // Determine status based on subscription status
        let status: 'active' | 'inactive' | 'suspended';
        if (business.subscription_status === 'active' || business.subscription_status === 'trialing') {
          status = 'active';
        } else if (business.subscription_status === 'past_due') {
          status = 'inactive';
        } else if (business.subscription_status === 'canceled' || business.subscription_status === 'inactive') {
          status = 'suspended';
        } else {
          status = 'active'; // Default for businesses without subscription data
        }
        
        return {
          id: business.id,
          name: business.name,
          owner_email: owner?.email || '',
          owner_name: owner ? `${owner.first_name} ${owner.last_name}`.trim() : '',
          type: getPlanName(business.active_plan_id),
          created_at: business.created_at,
          user_count: 0, // Would need separate query
          booking_count: 0, // Would need separate query
          sales_amount: 0, // Would need separate query
          last_activity: business.updated_at || business.created_at,
          status,
          integrations: {
            gmail: searchGmailConnectedBusinessIds.has(business.id) || searchSmtpOauthBusinessIds.has(business.id),
            twilio: !!(business.twilio_enabled && 
                      business.twilio_account_sid && 
                      business.twilio_auth_token && 
                      business.VITE_TWILIO_PHONE_NUMBER),
            stripe: !!(business.stripe_connect_account_id && 
                      business.stripe_connect_onboarding_completed)
          }
        };
      });

      // Transform user data
      const userSummaries: UserSummary[] = (users || []).map(user => {
        const business = Array.isArray(user.businesses) ? user.businesses[0] : user.businesses;
        return {
          id: user.id,
          email: user.email,
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          business_name: business?.name || 'No Business',
          business_id: business?.id || '',
          created_at: user.created_at,
          last_sign_in_at: '',
          is_business_owner: false
        };
      });

      return {
        businesses: businessSummaries,
        users: userSummaries
      };
    } catch (error) {
      console.error('Error searching platform:', error);
      return { businesses: [], users: [] };
    }
  }

  /**
   * Get business deletion info (what data will be affected)
   */
  static async getBusinessDeletionInfo(businessId: string): Promise<{
    canDelete: boolean;
    warnings: string[];
    counts: {
      users: number;
      bookings: number;
      customers: number;
      services: number;
      resources: number;
    };
  } | null> {
    if (!supabase) return null;

    try {
      console.log('🔍 Checking deletion requirements for business:', businessId);

      // Get counts of related data
      const [
        { count: userCount },
        { count: bookingCount },
        { count: customerCount },
        { count: serviceCount },
        { count: resourceCount }
      ] = await Promise.all([
        supabase.from('users').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('bookings').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('is_deleted', false),
        supabase.from('customers').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('services').select('*', { count: 'exact', head: true }).eq('business_id', businessId),
        supabase.from('resources').select('*', { count: 'exact', head: true }).eq('business_id', businessId)
      ]);

      const counts = {
        users: userCount || 0,
        bookings: bookingCount || 0,
        customers: customerCount || 0,
        services: serviceCount || 0,
        resources: resourceCount || 0
      };

      const warnings: string[] = [];
      let canDelete = true;

      // Add warnings for data that will be deleted
      if (counts.bookings > 0) {
        warnings.push(`${counts.bookings} booking(s) will be permanently deleted`);
      }
      if (counts.customers > 0) {
        warnings.push(`${counts.customers} customer(s) will be permanently deleted`);
      }
      if (counts.services > 0) {
        warnings.push(`${counts.services} service(s) will be permanently deleted`);
      }
      if (counts.resources > 0) {
        warnings.push(`${counts.resources} resource(s) will be permanently deleted`);
      }
      if (counts.users > 0) {
        warnings.push(`${counts.users} user account(s) will be permanently deleted`);
      }

      // Business can always be deleted, but warn about consequences
      if (warnings.length === 0) {
        warnings.push('No associated data found. Business can be safely deleted.');
      }

      console.log('✅ Deletion info gathered:', { canDelete, warnings, counts });

      return { canDelete, warnings, counts };
    } catch (error) {
      console.error('❌ Error checking deletion requirements:', error);
      return null;
    }
  }

  /**
   * Delete a business and all its associated data
   */
  static async deleteBusiness(businessId: string, confirmDeletion: boolean = false): Promise<{
    success: boolean;
    message: string;
    deletedCounts?: {
      users: number;
      bookings: number;
      customers: number;
      services: number;
      resources: number;
    };
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    if (!confirmDeletion) {
      return { success: false, message: 'Deletion must be explicitly confirmed' };
    }

    try {
      console.log('🗑️ Starting business deletion process for:', businessId);

      // Get deletion info first
      const deletionInfo = await this.getBusinessDeletionInfo(businessId);
      if (!deletionInfo) {
        return { success: false, message: 'Could not verify business deletion requirements' };
      }

      // Start comprehensive deletion process to handle all foreign key constraints
      // We need to delete in the correct order to avoid foreign key violations
      
      const deletedCounts = { ...deletionInfo.counts };

      // 1. Delete all booking-related data first (as bookings often reference many tables)
      if (deletionInfo.counts.bookings > 0) {
        console.log('🗑️ Deleting bookings and related data...');
        
        // Delete booking items, time entries, and other booking-related data
        const tables = ['booking_items', 'time_entries', 'booking_notes', 'payments'];
        for (const table of tables) {
          try {
            await supabase.from(table).delete().eq('business_id', businessId);
            console.log(`✅ Deleted ${table} for business`);
          } catch (error) {
            console.log(`ℹ️ Table ${table} might not exist or have business_id`);
          }
        }
        
        // Now delete bookings
        const { error: bookingsError } = await supabase
          .from('bookings')
          .delete()
          .eq('business_id', businessId);
        
        if (bookingsError) {
          console.error('❌ Error deleting bookings:', bookingsError);
          throw new Error(`Failed to delete bookings: ${bookingsError.message}`);
        }
      }

      // 2. Delete all other business-related tables that might have foreign keys
      const businessTables = [
        'automations',
        'automation_steps', 
        'proposals',
        'invoices',
        'customers',
        'vehicles',
        'services',
        'resources',
        'materials',
        'business_settings',
        'invoice_settings',
        'email_templates',
        'sms_templates',
        'time_entries',
        'payroll_entries'
      ];

      for (const table of businessTables) {
        try {
          console.log(`🗑️ Deleting ${table}...`);
          const { error } = await supabase
            .from(table)
            .delete()
            .eq('business_id', businessId);
          
          if (error && !error.message.includes('does not exist')) {
            console.error(`❌ Error deleting ${table}:`, error);
            // Continue with other tables even if one fails
          } else {
            console.log(`✅ Deleted ${table} successfully`);
          }
        } catch (error) {
          console.log(`ℹ️ Table ${table} might not exist or might not have business_id column`);
        }
      }

      // 3. Delete users (but handle carefully to avoid auth issues)
      if (deletionInfo.counts.users > 0) {
        console.log('🗑️ Deleting user accounts...');
        const { error: usersError } = await supabase
          .from('users')
          .delete()
          .eq('business_id', businessId);
        
        if (usersError) {
          console.error('❌ Error deleting users:', usersError);
          // Don't throw here, continue with business deletion
          console.log('⚠️ Continuing with business deletion despite user deletion error');
        }
      }

      // 4. Finally, delete the business itself
      console.log('🗑️ Deleting business...');
      const { error: businessError } = await supabase
        .from('businesses')
        .delete()
        .eq('id', businessId);
      
      if (businessError) {
        console.error('❌ Error deleting business:', businessError);
        throw new Error(`Failed to delete business: ${businessError.message}`);
      }

      console.log('✅ Business deletion completed successfully');

      return {
        success: true,
        message: 'Business and all associated data deleted successfully',
        deletedCounts
      };

    } catch (error) {
      console.error('❌ Error during business deletion:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred during deletion'
      };
    }
  }

  /**
   * Impersonate a business owner (for super admin use)
   */
  static async impersonateBusinessOwner(businessId: string): Promise<{
    success: boolean;
    message: string;
    ownerData?: any;
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('👤 Starting impersonation for business:', businessId);

      // Get business information
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id, name, owner_id')
        .eq('id', businessId)
        .single();

      if (businessError || !business) {
        console.error('❌ Business not found:', businessError);
        return { success: false, message: 'Business not found' };
      }

      console.log('🏢 Business found:', business.name, 'Owner ID:', business.owner_id);

      let owner = null;

      // Try to find owner by owner_id first
      if (business.owner_id) {
        const { data: ownerData, error: ownerError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, business_id')
          .eq('id', business.owner_id)
          .single();

        if (!ownerError && ownerData) {
          owner = ownerData;
          console.log('✅ Found owner by owner_id:', owner.email);
        } else {
          console.log('⚠️ Owner not found by owner_id, trying fallback...');
        }
      }

      // Fallback: Find any user associated with this business (first user found)
      if (!owner) {
        console.log('🔍 Looking for any user associated with business...');
        
        const { data: businessUsers, error: usersError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, business_id')
          .eq('business_id', businessId)
          .limit(1);

        if (!usersError && businessUsers && businessUsers.length > 0) {
          owner = businessUsers[0];
          console.log('✅ Found fallback user:', owner.email);
        } else {
          console.error('❌ No users found for business:', usersError);
        }
      }

      // Final fallback: Create a temporary user session for this business
      if (!owner) {
        console.log('🆘 No users found, creating temporary admin session...');
        
        // Create a minimal user object for impersonation
        owner = {
          id: `temp_admin_${businessId}`,
          email: `admin@${business.name.toLowerCase().replace(/\s+/g, '')}.temp`,
          first_name: 'Temp',
          last_name: 'Admin',
          business_id: businessId
        };
        
        console.log('✅ Created temporary admin session:', owner.email);
      }

      return {
        success: true,
        message: 'Owner data retrieved successfully',
        ownerData: {
          id: owner.id,
          email: owner.email,
          first_name: owner.first_name,
          last_name: owner.last_name,
          business_id: businessId, // Ensure we use the correct business ID
          business_name: business.name
        }
      };

    } catch (error) {
      console.error('❌ Error during impersonation setup:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Delete a user completely from both auth.users and public.users
   */
  static async deleteUser(userId: string): Promise<{
    success: boolean;
    message: string;
  }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('🗑️ Starting user deletion for:', userId);

      // First, get the user's email before deletion
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('email')
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        console.error('❌ User not found:', userError);
        return { success: false, message: 'User not found' };
      }

      const userEmail = userData.email;
      console.log('📧 User email:', userEmail);

      // Delete from public.users first
      const { error: publicDeleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);

      if (publicDeleteError) {
        console.error('❌ Error deleting from public.users:', publicDeleteError);
        return { success: false, message: `Failed to delete user: ${publicDeleteError.message}` };
      }

      console.log('✅ Deleted from public.users');

      // Delete from auth.users using RPC (requires service role)
      try {
        const { error: authDeleteError } = await supabase.rpc('delete_auth_user', {
          user_id: userId
        });

        if (authDeleteError) {
          console.error('❌ Error deleting from auth.users:', authDeleteError);
          // Don't fail completely, just log the error
          console.log('⚠️ Could not delete from auth.users, but user is removed from public.users');
        } else {
          console.log('✅ Deleted from auth.users');
        }
      } catch (error) {
        console.log('⚠️ RPC function not available, auth user may still exist');
      }

      // Also delete from user_setup_status if exists
      try {
        await supabase
          .from('user_setup_status')
          .delete()
          .eq('user_id', userId);
        console.log('✅ Deleted from user_setup_status');
      } catch (error) {
        console.log('ℹ️ user_setup_status table might not exist or user not found');
      }

      console.log('✅ User deletion completed successfully');
      return {
        success: true,
        message: `User ${userEmail} deleted successfully`
      };

    } catch (error) {
      console.error('❌ Error during user deletion:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred during deletion'
      };
    }
  }

  /**
   * Enable trial for a business
   */
  static async enableTrial(
    businessId: string, 
    trialEndDate: string,
    adminUserId: string
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) {
      return { success: false, message: 'Database connection not available' };
    }

    try {
      console.log('🎯 SUPER ADMIN: Enabling trial for business:', businessId);
      console.log('🎯 SUPER ADMIN: Trial end date:', trialEndDate);
      console.log('🎯 SUPER ADMIN: Admin user ID:', adminUserId);

      // First, get the business details
      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('name, subscription_status, trial_enabled, trial_end_date')
        .eq('id', businessId)
        .single();

      if (businessError || !business) {
        return { success: false, message: 'Business not found' };
      }

      console.log('📋 SUPER ADMIN: Current business status:', {
        name: business.name,
        subscription_status: business.subscription_status,
        trial_enabled: business.trial_enabled,
        trial_end_date: business.trial_end_date
      });

      // Validate trial end date is in the future
      const endDate = new Date(trialEndDate);
      const now = new Date();
      
      if (endDate <= now) {
        return { success: false, message: 'Trial end date must be in the future' };
      }

      // Update the business with trial information
      const { error: updateError } = await supabase
        .from('businesses')
        .update({
          trial_enabled: true,
          trial_start_date: now.toISOString(),
          trial_end_date: endDate.toISOString(),
          trial_activated_by: adminUserId,
          trial_activated_at: now.toISOString(),
          subscription_status: 'active', // Set to active during trial
          subscription_last_checked: now.toISOString(),
          updated_at: now.toISOString()
        })
        .eq('id', businessId);

      if (updateError) {
        console.error('❌ SUPER ADMIN: Error updating business:', updateError);
        return { success: false, message: 'Failed to enable trial' };
      }

      // Update all resources for this business to have active subscription status
      const { error: resourcesError } = await supabase
        .from('resources')
        .update({
          subscription_status: 'active',
          updated_at: now.toISOString()
        })
        .eq('business_id', businessId);

      if (resourcesError) {
        console.error('❌ SUPER ADMIN: Error updating resources:', resourcesError);
        // Don't fail the entire operation, just log the error
        console.warn('Business trial enabled but some resources may not be updated');
      } else {
        console.log('✅ SUPER ADMIN: Updated subscription status for all resources');
      }

      // Log the trial activation in audit log
      const { error: auditError } = await supabase
        .from('trial_audit_log')
        .insert({
          business_id: businessId,
          admin_user_id: adminUserId,
          action: 'trial_activated',
          trial_start_date: now.toISOString(),
          trial_end_date: endDate.toISOString(),
          previous_end_date: business.trial_end_date,
          notes: `Trial enabled by super admin until ${endDate.toLocaleDateString()}`
        });

      if (auditError) {
        console.error('❌ SUPER ADMIN: Error logging trial activation:', auditError);
        // Don't fail the operation for audit logging issues
      }

      console.log('✅ SUPER ADMIN: Trial enabled successfully for business:', business.name);
      return { 
        success: true, 
        message: `Trial enabled for ${business.name} until ${endDate.toLocaleDateString()}` 
      };

    } catch (error) {
      console.error('❌ SUPER ADMIN: Error enabling trial:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  /**
   * Get trial audit log for a business
   */
  static async getTrialAuditLog(businessId: string) {
    if (!supabase) return [];

    try {
      const { data, error } = await supabase
        .from('trial_audit_log')
        .select(`
          *,
          admin_user:auth.users!trial_audit_log_admin_user_id_fkey(email)
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching trial audit log:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching trial audit log:', error);
      return [];
    }
  }
} 